// ./src/hooks/useDisplays.ts
import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { OP_RESULT_CODES } from "@sdflc/api-helpers";

import {
  Display,
  DisplayService,
  DisplayFilter,
  DisplayInput,
  Pagination,
} from "@/utils/api";
import { SortConfig } from "@/components/generic";
import { DEFAULT_PAGE_SIZE } from "@/config";

export interface UseDisplaysOptions {
  /** Venue ID to filter displays by */
  venueId?: string | undefined;

  /** Layout ID to filter displays by */
  layoutId?: string | undefined;

  /** Initial page size for pagination */
  pageSize?: number;

  /** Initial filters */
  initialFilters?: Partial<DisplayFilter>;

  /** Initial sort configuration */
  initialSort?: SortConfig[];

  /** Auto-load data on mount */
  autoLoad?: boolean;
}

export interface UseDisplaysReturn {
  // Data state
  display: Display | null;
  displays: Display[];
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  isEmpty: boolean;

  // Search and filters
  filters: Partial<DisplayFilter>;
  sortBy: SortConfig[];

  // Pagination
  currentPage: number;
  pageSize: number;

  // Operations state
  isCreating: boolean;
  isUpdating: boolean;
  removingDisplayId: string | null;
  selectedDisplay: Display | null;

  // Core operations (matching API method names exactly)
  get: (id: string) => Promise<void>;
  list: (
    filter?: Partial<DisplayFilter>,
    pagination?: Pagination,
    sortBy?: SortConfig[],
    reset?: boolean
  ) => Promise<void>;
  getMany: (ids: string[]) => Promise<Display[]>;
  create: (params: DisplayInput) => Promise<Display | null>;
  update: (id: string, params: DisplayInput) => Promise<Display | null>;
  remove: (id: string) => Promise<boolean>;

  // Additional convenience methods
  loadMore: () => Promise<void>;
  refresh: () => Promise<void>;

  // Filter operations
  setFilters: (filters: Partial<DisplayFilter>) => void;
  setSortBy: (sort: SortConfig[]) => void;

  // UI state management
  setSelectedDisplay: (display: Display | null) => void;

  // Helper operations
  reset: () => void;

  // Computed values
  displaysByVenue: Record<string, Display[]>;
  displaysByLayout: Record<string, Display[]>;

  // Error handling
  lastError: string | null;
  clearError: () => void;
}

const DEFAULT_SORT: SortConfig[] = [{ key: "displays.name", direction: "asc" }];

export const useDisplays = (
  options: UseDisplaysOptions = {}
): UseDisplaysReturn => {
  const {
    venueId,
    layoutId,
    pageSize = DEFAULT_PAGE_SIZE,
    initialFilters = {},
    initialSort = DEFAULT_SORT,
    autoLoad = true,
  } = options;

  // Use refs for option values to avoid dependency issues
  const venueIdRef = useRef(venueId);
  const layoutIdRef = useRef(layoutId);
  const pageSizeRef = useRef(pageSize);
  const sortByRef = useRef<SortConfig[]>(initialSort);

  // Update refs when options change
  useEffect(() => {
    venueIdRef.current = venueId;
  }, [venueId]);

  useEffect(() => {
    layoutIdRef.current = layoutId;
  }, [layoutId]);

  useEffect(() => {
    pageSizeRef.current = pageSize;
  }, [pageSize]);

  // Data state
  const [display, setDisplay] = useState<Display | null>(null);
  const [displays, setDisplays] = useState<Display[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);

  // Search and filters
  const [filters, setFilters] = useState<Partial<DisplayFilter>>(() => {
    const filter = { ...initialFilters };
    if (venueId) {
      filter.venueId = [venueId];
    }
    if (layoutId) {
      filter.layoutId = [layoutId];
    }
    return filter;
  });
  const [sortBy, setSortBy] = useState<SortConfig[]>(initialSort);

  // Update sortByRef when sortBy state changes
  useEffect(() => {
    sortByRef.current = sortBy;
  }, [sortBy]);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);

  // Operations state
  const [isCreating, setIsCreating] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [removingDisplayId, setRemovingDisplayId] = useState<string | null>(
    null
  );
  const [selectedDisplay, setSelectedDisplay] = useState<Display | null>(null);

  // Error handling
  const [lastError, setLastError] = useState<string | null>(null);

  // Computed values
  const isEmpty = useMemo(
    () => displays.length === 0 && !loading,
    [displays.length, loading]
  );

  const displaysByVenue = useMemo(() => {
    return displays.reduce((acc, display) => {
      const venueId = display.venueId || "unknown";
      if (!acc[venueId]) {
        acc[venueId] = [];
      }
      acc[venueId].push(display);
      return acc;
    }, {} as Record<string, Display[]>);
  }, [displays]);

  const displaysByLayout = useMemo(() => {
    return displays.reduce((acc, display) => {
      const layoutId = display.layoutId || "unknown";
      if (!acc[layoutId]) {
        acc[layoutId] = [];
      }
      acc[layoutId].push(display);
      return acc;
    }, {} as Record<string, Display[]>);
  }, [displays]);

  // Helper function to update display in list if it exists, or add it if not
  const updateOrAddDisplayInList = useCallback((updatedDisplay: Display) => {
    setDisplays((prev) => {
      const existingIndex = prev.findIndex((d) => d.id === updatedDisplay.id);
      if (existingIndex >= 0) {
        const newDisplays = [...prev];
        newDisplays[existingIndex] = updatedDisplay;
        return newDisplays;
      } else {
        return [updatedDisplay, ...prev];
      }
    });
  }, []);

  // Helper function to remove display from list if it exists
  const removeDisplayFromList = useCallback((displayId: string) => {
    setDisplays((prev) => prev.filter((d) => d.id !== displayId));
  }, []);

  // Clear error function
  const clearError = useCallback(() => {
    setLastError(null);
  }, []);

  // Reset function
  const reset = useCallback(() => {
    setDisplay(null);
    setDisplays([]);
    setLastError(null);
    setLoading(false);
    setLoadingMore(false);
    setHasMore(false);
    setCurrentPage(1);
    setIsCreating(false);
    setIsUpdating(false);
    setRemovingDisplayId(null);
    setSelectedDisplay(null);
  }, []);

  // Get single display (matching API method name)
  const get = useCallback(
    async (id: string) => {
      setLoading(true);
      setLastError(null);

      try {
        const result = await DisplayService.get(id);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const displayData = result.data[0] as Display;
          setDisplay(displayData);
          updateOrAddDisplayInList(displayData);
          setLastError(null);
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to load display";
          setLastError(errorMessage);
          setDisplay(null);
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Failed to load display";
        setLastError(errorMessage);
        setDisplay(null);
      } finally {
        setLoading(false);
      }
    },
    [updateOrAddDisplayInList]
  );

  // List displays (matching API method name) - NO dependencies
  const list = useCallback(
    async (
      filter: Partial<DisplayFilter> = {},
      pagination?: Pagination,
      sortByParams?: SortConfig[],
      reset: boolean = false
    ) => {
      const targetPage = pagination?.page || 1;
      const targetPageSize = pagination?.pageSize || pageSizeRef.current;
      const isAppending = !reset;

      if (isAppending) {
        setLoadingMore(true);
      } else {
        setLoading(true);
        if (reset) {
          setDisplays([]);
          setCurrentPage(1);
        }
      }

      const combinedFilter = { ...filter };
      if (venueIdRef.current && !combinedFilter.venueId) {
        combinedFilter.venueId = [venueIdRef.current];
      }
      if (layoutIdRef.current && !combinedFilter.layoutId) {
        combinedFilter.layoutId = [layoutIdRef.current];
      }

      setFilters(combinedFilter);
      if (sortByParams) setSortBy(sortByParams);

      const currentSortBy = sortByParams || sortByRef.current;

      try {
        const paginationParams: Pagination = {
          page: targetPage,
          pageSize: targetPageSize,
        };

        const sortParams = currentSortBy.map((sort) => ({
          name: sort.key,
          order: sort.direction.toUpperCase() as "ASC" | "DESC",
        }));

        const result = await DisplayService.list(
          combinedFilter as DisplayFilter,
          paginationParams,
          sortParams
        );

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const newDisplays = Array.isArray(result.data) ? result.data : [];

          if (isAppending) {
            setDisplays((prev) => {
              const existingIds = new Set(prev.map((d) => d.id));
              const uniqueNewDisplays = newDisplays.filter(
                (d) => !existingIds.has(d.id)
              );
              return [...prev, ...uniqueNewDisplays];
            });
          } else {
            setDisplays(newDisplays);
          }

          setHasMore(newDisplays.length === targetPageSize);
          setCurrentPage(targetPage);
          setLastError(null);
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to load displays";
          setLastError(errorMessage);
          if (!isAppending) setDisplays([]);
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Failed to load displays";
        setLastError(errorMessage);
        if (!isAppending) setDisplays([]);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    []
  );

  // Get many displays by IDs (matching API method name)
  const getMany = useCallback(
    async (ids: string[]): Promise<Display[]> => {
      if (ids.length === 0) {
        return [];
      }

      setLoading(true);
      setLastError(null);

      try {
        const result = await DisplayService.getMany(ids);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const loadedDisplays = Array.isArray(result.data) ? result.data : [];
          loadedDisplays.forEach((display) => {
            updateOrAddDisplayInList(display);
          });
          setLastError(null);
          return loadedDisplays;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to load displays";
          setLastError(errorMessage);
          return [];
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Failed to load displays";
        setLastError(errorMessage);
        return [];
      } finally {
        setLoading(false);
      }
    },
    [updateOrAddDisplayInList]
  );

  // Create display (matching API method name)
  const create = useCallback(
    async (params: DisplayInput): Promise<Display | null> => {
      setIsCreating(true);
      setLastError(null);

      try {
        const displayToCreate = {
          ...params,
          venueId: params.venueId || venueIdRef.current || null,
          layoutId: params.layoutId || layoutIdRef.current,
        };

        const result = await DisplayService.create(displayToCreate);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const newDisplay = result.data[0] as Display;
          setDisplay(newDisplay);
          setDisplays((prev) => [newDisplay, ...prev]);
          setLastError(null);
          return newDisplay;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to create display";
          setLastError(errorMessage);
          return null;
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Failed to create display";
        setLastError(errorMessage);
        return null;
      } finally {
        setIsCreating(false);
      }
    },
    []
  );

  // Update display (matching API method name)
  const update = useCallback(
    async (id: string, params: DisplayInput): Promise<Display | null> => {
      if (!id) {
        setLastError("Display ID is required for updating");
        return null;
      }

      setIsUpdating(true);
      setLastError(null);

      try {
        const result = await DisplayService.update(id, params);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const updatedDisplay = result.data[0] as Display;
          setDisplay(updatedDisplay);
          updateOrAddDisplayInList(updatedDisplay);
          setLastError(null);
          return updatedDisplay;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to update display";
          setLastError(errorMessage);
          return null;
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Failed to update display";
        setLastError(errorMessage);
        return null;
      } finally {
        setIsUpdating(false);
      }
    },
    [updateOrAddDisplayInList]
  );

  // Remove display (matching API method name)
  const remove = useCallback(
    async (id: string): Promise<boolean> => {
      if (!id) {
        setLastError("Display ID is required for removal");
        return false;
      }

      setRemovingDisplayId(id);
      setLastError(null);

      try {
        const result = await DisplayService.remove(id);

        if (result.code === OP_RESULT_CODES.OK) {
          setDisplay(null);
          removeDisplayFromList(id);
          setLastError(null);
          return true;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to remove display";
          setLastError(errorMessage);
          return false;
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Failed to remove display";
        setLastError(errorMessage);
        return false;
      } finally {
        setRemovingDisplayId(null);
      }
    },
    [removeDisplayFromList]
  );

  // Load more displays
  const loadMore = useCallback(async () => {
    if (!loadingMore && hasMore) {
      const nextPage = currentPage + 1;
      await list(
        filters,
        { page: nextPage, pageSize: pageSizeRef.current },
        sortByRef.current,
        false
      );
    }
  }, [list, loadingMore, hasMore, currentPage, filters]);

  // Refresh displays
  const refresh = useCallback(async () => {
    await list(
      filters,
      { page: 1, pageSize: pageSizeRef.current },
      sortByRef.current,
      true
    );
  }, [list, filters]);

  // Auto-load effect
  const [hasAutoLoaded, setHasAutoLoaded] = useState(false);

  useEffect(() => {
    if (autoLoad && !hasAutoLoaded) {
      setHasAutoLoaded(true);
      const autoLoadFilters = { ...initialFilters };
      if (venueId) {
        autoLoadFilters.venueId = [venueId];
      }
      if (layoutId) {
        autoLoadFilters.layoutId = [layoutId];
      }

      list(autoLoadFilters, { page: 1, pageSize }, initialSort, true);
    }
  }, [autoLoad, hasAutoLoaded, list, pageSize, initialSort]);

  return {
    display,
    displays,
    loading,
    loadingMore,
    hasMore,
    isEmpty,
    filters,
    sortBy,
    currentPage,
    pageSize,
    isCreating,
    isUpdating,
    removingDisplayId,
    selectedDisplay,
    get,
    list,
    getMany,
    create,
    update,
    remove,
    loadMore,
    refresh,
    setFilters,
    setSortBy,
    setSelectedDisplay,
    reset,
    displaysByVenue,
    displaysByLayout,
    lastError,
    clearError,
  };
};
